// inkfmt/v1 data model. See FORMAT.md for the normative spec.
// This module has no DOM or storage dependencies.

export const FORMAT_ID = 'inkfmt/v1' as const;

export type UUID = string;
/** Milliseconds since the Unix epoch. May be fractional. */
export type EpochMs = number;

/** Fields shared by every stored record. */
export interface BaseRecord {
  id: UUID;
  createdAt: EpochMs;
  updatedAt: EpochMs;
  /** Soft delete. Records are never purged. */
  deletedAt?: EpochMs;
  /**
   * Local change counter. Assigned from a per-device, strictly increasing
   * counter on every write. Meaningful only inside one device's database;
   * not part of exported files.
   */
  rev: number;
}

export interface DeviceInfo {
  id: UUID;
  userAgent: string;
  devicePixelRatio: number;
  screen: { width: number; height: number; availWidth: number; availHeight: number };
  maxTouchPoints: number;
  recordedAt: EpochMs;
}

export type Template = 'blank' | 'ruled' | 'grid' | 'dotted';
export const TEMPLATES: readonly Template[] = ['blank', 'ruled', 'grid', 'dotted'];

export interface Notebook extends BaseRecord {
  format: typeof FORMAT_ID;
  title: string;
  /** Every device that has written to this notebook, keyed by device id. */
  devices: Record<UUID, DeviceInfo>;
  defaultTemplate: Template;
}

export interface PageSize {
  width: number;
  height: number;
}

/** A4 portrait: 1 unit = 0.21 mm. */
export const A4: PageSize = { width: 1000, height: 1414 };
export const MM_PER_UNIT = 0.21;

export interface Page extends BaseRecord {
  notebookId: UUID;
  /** 0-based position in the notebook. */
  index: number;
  template: Template;
  size: PageSize;
}

export type Tool = 'pen' | 'highlighter';
export type PointerKind = 'pen' | 'mouse' | 'touch';

/** Row-major 2×3 affine [a, b, c, d, e, f]: x' = a·x + c·y + e, y' = b·x + d·y + f. */
export type Affine = [number, number, number, number, number, number];

export const CHANNELS = ['x', 'y', 'pressure', 'tiltX', 'tiltY', 't'] as const;
export type Channels = typeof CHANNELS;
export const STRIDE = CHANNELS.length;

/**
 * Mapping from page units to CSS client pixels at capture time:
 * clientX = x·zoom + offsetX, clientY = y·zoom + offsetY.
 */
export interface CaptureView {
  zoom: number;
  offsetX: number;
  offsetY: number;
}

/** Raw pen samples shared by strokes and hover segments. */
export interface SampleRun {
  pageId: UUID;
  notebookId: UUID;
  deviceId: UUID;
  pointerType: PointerKind;
  /** Epoch ms of the first sample. */
  startTime: EpochMs;
  channels: Channels;
  /** Interleaved samples, stride 6, exactly as received. t is relative to startTime. */
  points: Float32Array;
  capture: CaptureView;
}

export interface Stroke extends BaseRecord, SampleRun {
  tool: Tool;
  color: string;
  baseWidth: number;
  /** Render-time transform (e.g. lasso move). Never baked into points. */
  transform?: Affine;
}

/** Pen samples recorded while the pen was in range but not touching. Never rendered. */
export interface HoverSegment extends BaseRecord, SampleRun {
  prevStrokeId: UUID | null;
  nextStrokeId: UUID | null;
}

/** Strips the local-only rev field. Used by the format layer. */
export type Exported<T extends BaseRecord> = Omit<T, 'rev'>;
